<script module lang="ts">
  const fetchData = async ({ query, countryCode }: { query: string; countryCode: string }) => {
    const url = new URL('/api/search', window.location.origin);
    url.searchParams.append('query', query);
    url.searchParams.append('locale', countryCode);

    const response = await fetch(url.toString());

    if (!response.ok) {
      throw new Error('Failed to fetch search results');
    }

    return (await response.json()) as SearchResult[];
  };
</script>

<script lang="ts">
  import { fade, slide } from 'svelte/transition';
  import { Cross, Search, Spinner } from '@athena-lib/Icons';
  import Overlay from '@athena-lib/Overlay/Overlay.svelte';
  import Stack from '@athena-lib/Stack/Stack.svelte';
  import { StackItem } from '@athena-lib/Stack';
  import Button from '@athena-lib/Button/Button/Button.svelte';
  import debouncedFunc from '@athena-lib/timing-utils';

  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import { getLocalizationContext } from '@athena-lib/i18n';
  import SearchResultItem from './SearchResultItem.svelte';
  import type { SearchBarProps, SearchResult } from './types';

  const { searchQuery, setSearchQuery, onClose, onResultClicked }: SearchBarProps = $props();

  const translations = getCommonTranslations();
  const localizationContext = getLocalizationContext();

  const CLOSE_ANIMATION_DELAY = 50;
  const DEBOUNCE_TIME = 850;
  const MIN_QUERY_LENGTH = 3;
  const popularSuggestions = [
    translations.searchSuggestionOne,
    translations.searchSuggestionTwo,
    translations.searchSuggestionThree,
    translations.searchSuggestionFour,
    translations.searchSuggestionFive,
  ];

  let inputElem = $state<HTMLInputElement>();
  const displayPopularSuggestions = $derived(
    searchQuery !== undefined && searchQuery.length <= MIN_QUERY_LENGTH,
  );
  const searchResult: Promise<SearchResult[]> = $derived(
    searchQuery && searchQuery.length > MIN_QUERY_LENGTH
      ? fetchData({ query: searchQuery, countryCode: localizationContext.countryCode })
      : Promise.resolve([]),
  );

  const showClearButton = $derived(inputElem && inputElem.value.length > 0);

  const debouncedSearch = debouncedFunc((query: string | undefined) => {
    setSearchQuery(query);
  }, DEBOUNCE_TIME);

  function selectPopularSuggestion(suggestion: string) {
    if (!inputElem) {
      return;
    }
    inputElem.value = '';
    const animateTyping = (index = 0) => {
      if (!inputElem) {
        return;
      }
      if (index < suggestion.length) {
        inputElem.value += suggestion[index];
        requestAnimationFrame(() => animateTyping(index + 1));
      } else {
        setSearchQuery(suggestion);
      }
    };
    animateTyping();
  }

  function clearSearchInput() {
    inputElem?.focus();
    setSearchQuery('');
  }

  function keyHandler(event: KeyboardEvent, func: () => void) {
    if (event.code === 'Enter') {
      func();
    }
  }
</script>

{#snippet popularSuggestionsSection()}
  <div class="mb-2 mt-1 w-auto md:my-0" transition:fade={{ duration: 50, delay: 50 }}>
    <h6 class="uppercase text-gray-600">{translations.popularSearches}</h6>
    <Stack class="list-none p-0" space="none">
      {#each popularSuggestions as suggestion}
        <StackItem class="my-1 cursor-pointer rounded hover:bg-gray-300">
          <button
            type="button"
            class="w-full text-left"
            onclick={() => selectPopularSuggestion(suggestion)}
            onkeydown={(event) => keyHandler(event, () => selectPopularSuggestion(suggestion))}
          >
            {suggestion}
          </button>
        </StackItem>
      {/each}
    </Stack>
  </div>
{/snippet}

<Overlay isOpen={searchQuery !== undefined} {onClose} label="Search">
  <div
    class="fixed mx-auto flex w-full flex-col items-start justify-evenly bg-white px-2 py-6 shadow-2xl sm:items-center"
    transition:slide={{ duration: 300, axis: 'y' }}
  >
    <div class="container">
      <div class="flex items-center justify-between">
        <img
          width="150px"
          height="90px"
          src="https://d6ce0no7ktiq.cloudfront.net/images/web/logos/stickerapp.png"
          alt="StickerApp Logo"
          class="hidden md:block"
        />
        <div class="relative flex flex-col justify-center overflow-hidden">
          <div class="max-w-sm space-y-3">
            <div class="relative p-1">
              <!-- Ignoring linter rule here as do want the autofocus functionality -->
              <!-- and since this component is conditionally rendered it is not really an issue.  -->
              <!-- svelte-ignore a11y_autofocus -->
              {#key searchQuery}
                <input
                  type="text"
                  bind:this={inputElem}
                  value={searchQuery}
                  oninput={(e) => {
                    debouncedSearch(e.currentTarget.value);
                  }}
                  id="hs-input-with-leading-and-trailing-icon"
                  name="hs-input-with-leading-and-trailing-icon"
                  class="block w-full rounded-3xl border-gray-500 bg-gray-200 px-4 py-3 pe-16 ps-11 text-sm shadow-sm focus:z-10 focus:border-gray-600 focus:ring-gray-700 disabled:pointer-events-none disabled:opacity-50"
                  placeholder={translations.searchPlaceholder}
                  autofocus
                />
              {/key}

              <div
                class="absolute inset-y-0 start-0 z-20 flex items-center ps-4 text-gray-500 dark:text-neutral-500"
              >
                <Search sizeClass="w-6 h-6" />
              </div>

              <div
                class="absolute inset-y-0 end-0 z-20 flex items-center pe-4"
                class:hidden={!showClearButton}
              >
                <button
                  type="button"
                  class="text-gray-500 dark:text-neutral-500"
                  onclick={clearSearchInput}><Cross sizeClass="w-4 h-4" /></button
                >
              </div>
            </div>
          </div>
        </div>
        <Button
          class="font-helvetica cursor-pointer py-2 hover:animate-pulse"
          color="white"
          onClick={onClose}
          size="small"
        >
          {translations.hide}
        </Button>
      </div>
    </div>

    {#if displayPopularSuggestions}
      {#if searchQuery?.trim() !== ''}
        <div
          class="pb-gutter text-gray-500"
          transition:slide={{ duration: 350, axis: 'y', delay: CLOSE_ANIMATION_DELAY }}
        >
          <h6 class="pl-1">{translations.noResults}</h6>
        </div>
      {/if}
      {@render popularSuggestionsSection()}
    {/if}
    {#await searchResult}
      <div class="my-2 self-center">
        <Spinner sizeClass="w-6 h-6" />
      </div>
    {:then searchResults}
      {#if searchResults.length > 0}
        <div class="container flex w-full flex-row sm:justify-start md:justify-center">
          <ul
            class="m-0 py-3 md:py-0 lg:w-2/3"
            transition:slide={{ duration: 350, axis: 'y', delay: CLOSE_ANIMATION_DELAY }}
          >
            {#key searchResults}
              {#each searchResults.slice(0, 3) as item, index}
                <SearchResultItem
                  {item}
                  onClick={() => onResultClicked(item, index, inputElem?.value)}
                />
              {/each}
            {/key}
          </ul>
        </div>
      {/if}
    {:catch _err}
      {translations.anErrorHasOccured}
    {/await}
  </div>
</Overlay>
